// src/audio/MediaSessionManager.js
//
// Keeps the OS media session in sync with AudioEngine so the lock screen and
// Control Center always show the correct title and respond to play/pause/stop.
//
// Platform breakdown
// ──────────────────
// Web
//   Uses the W3C Media Session API (navigator.mediaSession). The browser shows
//   a media card in the OS notification area / lock screen with play, pause,
//   and stop buttons wired to AudioEngine.
//
// iOS (native)
//   react-native-track-player (RNTP) registers MPRemoteCommandCenter handlers
//   at app startup (see TrackPlayerService.js + index.js). iOS surfaces the
//   active audio session (played via expo-av) as a Now Playing card on the lock
//   screen and in Control Center. This module keeps the card title accurate via
//   TrackPlayer.updateNowPlayingMetadata() and mirrors play/pause state.
//
// Android (native)
//   RNTP starts a foreground MediaSession service that shows a persistent media
//   notification with play/pause/stop controls. Title and playback state are
//   kept in sync the same way as iOS.

import { Platform, Image } from 'react-native';
import { AudioEngine } from './AudioEngine';
import { initLockScreenControls, updateLockScreen } from './LockScreenControls';

// Per-sound-ID artwork map — resolves to the background image that best
// represents each category. Falls back to the app icon when no match is found.
// Keys match the `id` fields in src/data/sounds.js.
//
// Every sound ID in the catalog should have an entry here so the Android
// media notification always shows a themed image rather than the generic
// app icon.  Use the same image for sounds in the same category.
export const SOUND_ARTWORK = {
  // Sleep / nature
  rain:               require('../../assets/backgrounds/bg_sleep.jpg'),
  birds:              require('../../assets/backgrounds/bg_sleep.jpg'),
  water:              require('../../assets/backgrounds/bg_sleep.jpg'),
  'nature-sounds':    require('../../assets/backgrounds/bg_sleep.jpg'),
  fireplace:          require('../../assets/backgrounds/bg_sleep.jpg'),
  'wind-leaves':      require('../../assets/backgrounds/bg_sleep.jpg'),
  'ocean-waves':      require('../../assets/backgrounds/bg_sleep.jpg'),
  'sleep-meditation': require('../../assets/backgrounds/bg_sleep.jpg'),
  'night-crickets':   require('../../assets/backgrounds/bg_sleep.jpg'),
  // Music
  melody:             require('../../assets/backgrounds/bg_music.jpg'),
  piano:              require('../../assets/backgrounds/bg_music.jpg'),
  // Study
  'study-lofi':       require('../../assets/backgrounds/bg_study.jpg'),
  'study-white-noise':require('../../assets/backgrounds/bg_study.jpg'),
  'coffee-shop':      require('../../assets/backgrounds/bg_study.jpg'),
  'calm-focus':       require('../../assets/backgrounds/bg_study.jpg'),
  'study-jazz':       require('../../assets/backgrounds/bg_study.jpg'),
  // Kids
  'kids-lullaby':     require('../../assets/backgrounds/bg_kids.jpg'),
  'kids-rain':        require('../../assets/backgrounds/bg_kids.jpg'),
  'kids-heartbeat':   require('../../assets/backgrounds/bg_kids.jpg'),
  'kids-ocean-waves': require('../../assets/backgrounds/bg_kids.jpg'),
};

const DEFAULT_ARTWORK = require('../../assets/icon.png');

class MediaSessionManagerClass {
  constructor() {
    this._unsubscribeEngine = null;
    this._currentTitle = '';
    this._ready = false;
    // Monotonically-increasing counter used to detect stale async updates.
    // When the active category changes rapidly, the onChange handler increments
    // this before dispatching. Any in-flight updateLockScreen call whose
    // generation no longer matches is silently dropped so Android's
    // BitmapFactory never receives a queued request for superseded artwork.
    this._updateSeq = 0;
  }

  // Call once, after AudioEngine.init() resolves.
  async init() {
    // Set up RNTP for native platforms (no-op on web).
    await initLockScreenControls();
    this._ready = true;

    // Subscribe to AudioEngine state changes and sync the OS media card.
    this._unsubscribeEngine = AudioEngine.onChange((layers) => {
      const title = this._buildTitle(layers);
      const isPlaying = AudioEngine.isPlaying();
      const artworkUri = this._resolveArtworkUri(this._buildArtwork(layers));

      this._currentTitle = title;

      if (Platform.OS === 'web') {
        this._updateWebSession(title, isPlaying, artworkUri);
      } else {
        // Stamp this update with the current sequence number BEFORE the async
        // call so that any newer onChange that fires while updateLockScreen is
        // awaiting the RNTP bridge can cancel this one.  This prevents Android's
        // BitmapFactory from receiving queued requests for superseded artwork,
        // ensuring the thumbnail switches within ~1 second of a category change.
        const seq = ++this._updateSeq;
        this._updateLockScreenIfCurrent(seq, { title, isPlaying, artworkUri });
      }
    });

    // Run an initial sync in case audio was already playing when we mounted.
    //
    // Android force-close + relaunch edge case:
    //   The OS can kill the JS context while leaving the RNTP foreground
    //   service alive. When the JS context restarts, AudioEngine has not yet
    //   restored the persisted mix (that happens asynchronously after init).
    //   If we push a sync with zero layers here, the Android notification
    //   thumbnail would briefly show the default app-icon artwork.
    //
    //   Guard: skip the initial push when no layers are active yet. The
    //   onChange subscription set up above will fire as soon as AudioEngine
    //   restores the mix, producing the correct artwork within 1–2 seconds.
    //   On a normal first launch (nothing persisted) the notification will
    //   remain blank until the user starts playing — same behaviour as before.
    const initial = AudioEngine.getState();
    if (initial && initial.length > 0) {
      this._currentTitle = this._buildTitle(initial);
      const initialArtworkUri = this._resolveArtworkUri(this._buildArtwork(initial));
      if (Platform.OS === 'web') {
        this._updateWebSession(this._currentTitle, AudioEngine.isPlaying(), initialArtworkUri);
      } else {
        updateLockScreen({ title: this._currentTitle, isPlaying: AudioEngine.isPlaying(), artworkUri: initialArtworkUri });
      }
    }
  }

  // Tear down listeners (call when unmounting, if ever needed).
  destroy() {
    if (this._unsubscribeEngine) {
      this._unsubscribeEngine();
      this._unsubscribeEngine = null;
    }
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.mediaSession) {
      ['play', 'pause', 'stop'].forEach((action) => {
        try { navigator.mediaSession.setActionHandler(action, null); } catch (_) {}
      });
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  // Calls updateLockScreen only if `seq` still matches the latest update
  // sequence number.  If the active category changed again while the previous
  // RNTP bridge call was in flight, the stale request is dropped here so
  // Android's BitmapFactory only ever receives the most-recent artwork URI.
  async _updateLockScreenIfCurrent(seq, { title, isPlaying, artworkUri }) {
    // Pre-flight check — drop immediately if already superseded.
    if (seq !== this._updateSeq) {
      if (__DEV__) {
        console.log('[MediaSessionManager] dropping stale artwork update (pre-flight), seq:', seq, 'current:', this._updateSeq);
      }
      return;
    }
    await updateLockScreen({ title, isPlaying, artworkUri });
    // Post-flight log only — updateLockScreen is fire-and-forget; a superseded
    // seq here means the OS received the call but a newer one will follow
    // immediately after, so the final notification state will still be correct.
    if (__DEV__ && seq !== this._updateSeq) {
      console.log('[MediaSessionManager] artwork update was superseded in flight, seq:', seq, 'current:', this._updateSeq);
    }
  }

  _buildTitle(layers) {
    if (!layers || layers.length === 0) return 'SleepMixBlueprint';
    const names = layers.map((l) => l.name).filter(Boolean);
    if (names.length === 0) return 'SleepMixBlueprint';
    // "Rain", "Rain + White Noise", "Rain + White Noise + Birds", ...
    // Cap at 3 names to keep the lock-screen title readable.
    const shown = names.slice(0, 3);
    const title = shown.join(' + ');
    return names.length > 3 ? `${title} +${names.length - 3}` : title;
  }

  // Pick the best artwork asset for the current set of active layers.
  // Preference order:
  //   1. A custom artworkUri attached to the layer (user-imported tracks with a cover).
  //   2. The first layer whose sound ID has a dedicated image in SOUND_ARTWORK.
  //   3. The default app icon so the card is never blank.
  _buildArtwork(layers) {
    if (layers && layers.length > 0) {
      // Custom artwork URI from a user-imported track — return as-is (already a string).
      for (const layer of layers) {
        if (layer.artworkUri) return layer.artworkUri;
      }
      // Built-in sound artwork (require() asset reference).
      for (const layer of layers) {
        const asset = SOUND_ARTWORK[layer.id];
        if (asset) return asset;
      }
    }
    return DEFAULT_ARTWORK;
  }

  // Resolve an artwork reference to a URI string that both the web
  // MediaSession API and RNTP's updateNowPlayingMetadata can consume.
  // Accepts either:
  //   • A plain URI string (user-imported track cover) — returned directly.
  //   • A require() asset reference (number or object) — resolved via Image.resolveAssetSource.
  _resolveArtworkUri(asset) {
    // Plain string URI (e.g. file:// path for a user-chosen cover image).
    if (typeof asset === 'string') {
      if (__DEV__) console.log('[MediaSessionManager] custom artwork URI (passthrough):', asset);
      return asset;
    }
    try {
      const source = Image.resolveAssetSource(asset);
      const uri = source && source.uri ? source.uri : null;
      if (__DEV__) {
        if (uri) {
          console.log('[MediaSessionManager] artwork URI resolved:', uri);
        } else {
          console.warn('[MediaSessionManager] artwork URI could not be resolved — lock screen will show placeholder.');
        }
      }
      return uri;
    } catch (e) {
      if (__DEV__) console.warn('[MediaSessionManager] Image.resolveAssetSource error:', e?.message ?? e);
      return null;
    }
  }

  // ── Web (navigator.mediaSession) ──────────────────────────────────────────

  _updateWebSession(title, isPlaying, artworkUri) {
    if (typeof navigator === 'undefined' || !navigator.mediaSession) return;
    try {
      const artworkList = artworkUri
        ? [{ src: artworkUri, sizes: '512x512', type: 'image/jpeg' }]
        : [];

      navigator.mediaSession.metadata = new MediaMetadata({
        title,
        artist: 'SleepMixBlueprint',
        album: 'Ambient Sleep Sounds',
        artwork: artworkList,
      });

      navigator.mediaSession.setActionHandler('play', () => AudioEngine.resumeAll());
      navigator.mediaSession.setActionHandler('pause', () => AudioEngine.pauseAll());
      navigator.mediaSession.setActionHandler('stop', () => AudioEngine.stopAll({ fadeMs: 2000 }));

      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch (_) {
      // mediaSession not supported — no-op.
    }
  }
}

export const MediaSessionManager = new MediaSessionManagerClass();
